import React, { useEffect, useMemo, useState } from 'react'
import { gql } from 'graphql-request'
import { useSearchParams } from 'react-router'
import { History, Search, X, CheckCircle2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Button } from '@/components/ui/button'

/**
 * The activity log: who did what, when, to whom. Every mutation and every sign-in is one
 * entry (server: activity.ts). Filters live in the URL so the Users overview can link
 * here with `?view=activity&userId=<id>` and so a filtered view can be shared.
 */

export interface ActivityEntry {
  id: string
  at: string
  durationMs: number | null
  kind: 'mutation' | 'sign_in'
  action: string
  actor: { userId: number | null; email: string | null; isAdmin: boolean }
  input: Record<string, unknown> | null
  subjects: {
    userId: number | null
    userEmail: string | null
    organisationId: number | null
    organisationName: string | null
    moduleId: number | null
    subscriptionId: number | null
    joinLinkId: number | null
  }
  ok: boolean
  error: string | null
  meta: { provider?: string; joinLinkId?: number; createdAccount?: boolean } | null
}

interface ActivityPage {
  entries: ActivityEntry[]
  totalCount: number
}

const PAGE_SIZE = 50

const GET_ACTIVITY = gql`
  query getActivity($filter: ActivityFilterInput, $limit: Int, $offset: Int) {
    getActivity(filter: $filter, limit: $limit, offset: $offset) {
      totalCount
      entries {
        id
        at
        durationMs
        kind
        action
        actor {
          userId
          email
          isAdmin
        }
        input
        subjects {
          userId
          userEmail
          organisationId
          organisationName
          moduleId
          subscriptionId
          joinLinkId
        }
        ok
        error
        meta
      }
    }
  }
`

const GET_ACTIVITY_ACTIONS = gql`
  query getActivityActions {
    getActivityActions
  }
`

/** Readable names for the actions an admin will look for; anything else is split from camelCase. */
export const ACTION_LABELS: Record<string, string> = {
  sign_in: 'Signed in',
  createUser: 'Created user',
  attachUserToOrganisation: 'Attached user to organisation',
  detachUserFromOrganisation: 'Removed user from organisation',
  updateUserCoachStatus: 'Changed coach role',
  createOrganisation: 'Created organisation',
  deactivateOrganisation: 'Deactivated or reactivated organisation',
  deleteOrganisation: 'Deleted organisation',
  updateOrganisationOnboardingVideos: 'Changed onboarding videos',
  setOrganisationPaymentAccount: 'Set payment account of organisation',
  createSubscription: 'Created subscription',
  deleteSubscription: 'Deleted subscription',
  connectUserToModule: 'Gave user a module seat',
  disconnectUserFromModule: 'Took module seat from user',
  createJoinLink: 'Created join link',
  revokeJoinLink: 'Revoked join link',
  redeemJoinToken: 'Redeemed join link',
  createModule: 'Created module',
  updateModule: 'Updated module',
  deleteModule: 'Deleted module',
  createTarget: 'Created target',
  updateTarget: 'Updated target',
  deleteTarget: 'Deleted target',
  startRoleplaySession: 'Started roleplay session',
  endRoleplaySession: 'Ended roleplay session',
  saveReview: 'Saved review',
  updateSettings: 'Changed settings',
  saveCoachWelcome: 'Saved coach welcome',
  clearTargetWelcomeCache: 'Cleared target welcome cache',
  clearModuleWelcomeCache: 'Cleared module welcome cache',
  createAvatar: 'Created avatar',
  updateAvatar: 'Updated avatar',
  deleteAvatar: 'Deleted avatar',
  createPaymentAccount: 'Created payment account',
  refreshPaymentAccount: 'Refreshed payment account',
  archivePaymentAccount: 'Archived payment account',
  rotatePaymentAccountOnboardingLink: 'Rotated payment onboarding link',
  createMinutePackage: 'Created minute package',
  updateMinutePackage: 'Updated minute package',
  createMinuteCheckout: 'Started minute purchase',
  confirmMinutePurchase: 'Confirmed minute purchase',
  createSubscriptionPlan: 'Created subscription plan',
  updateSubscriptionPlan: 'Updated subscription plan',
  createPlanCardTemplate: 'Created plan card template',
  updatePlanCardTemplate: 'Updated plan card template',
  duplicatePlanCardTemplate: 'Duplicated plan card template',
  setPlanCardTemplateArchived: 'Archived or restored plan card template',
  deletePlanCardTemplate: 'Deleted plan card template',
  setSubscriptionPlanCard: 'Set plan card of subscription plan',
  setPaywallContent: 'Changed paywall texts',
  setLoginPageContent: 'Changed sign-in page texts',
  createSubscriptionCheckout: 'Started subscription checkout',
  confirmSubscriptionCheckout: 'Confirmed subscription checkout',
  createSubscriptionPortalSession: 'Opened billing portal',
}

export const actionLabel = (action: string): string => {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action]
  const words = action
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const formatTime = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/** The URL-backed filter keys. `userId` is what the Users overview links with. */
const FILTER_KEYS = ['search', 'action', 'kind', 'userId', 'organisationId', 'from', 'to'] as const
type FilterKey = (typeof FILTER_KEYS)[number]

const ActivityView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [entries, setEntries] = useState<ActivityEntry[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [actions, setActions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Typed text goes to the URL only after a pause, so the query is not sent per keystroke
  const [searchText, setSearchText] = useState(searchParams.get('search') || '')

  const filter = useMemo(() => {
    const read = (key: FilterKey) => searchParams.get(key) || ''
    const asInt = (value: string) => (/^\d+$/.test(value) ? Number(value) : null)
    return {
      search: read('search') || null,
      action: read('action') || null,
      kind: read('kind') || null,
      userId: asInt(read('userId')),
      organisationId: asInt(read('organisationId')),
      from: read('from') ? new Date(read('from')).toISOString() : null,
      to: read('to') ? new Date(new Date(read('to')).getTime() + 86_400_000).toISOString() : null,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()])

  const setFilter = (key: FilterKey, value: string) => {
    setSearchParams((prev) => {
      if (value) prev.set(key, value)
      else prev.delete(key)
      return prev
    })
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if ((searchParams.get('search') || '') !== searchText.trim()) setFilter('search', searchText.trim())
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  useEffect(() => {
    authorizedGraphQLRequest<{ getActivityActions: string[] }>(GET_ACTIVITY_ACTIONS)
      .then((res) => setActions(res.getActivityActions || []))
      .catch((err) => console.error('Failed to load activity actions:', err))
  }, [])

  const fetchPage = async (offset: number) => {
    const append = offset > 0
    try {
      if (append) setLoadingMore(true)
      else {
        setLoading(true)
        setError(null)
      }
      const res = await authorizedGraphQLRequest<{ getActivity: ActivityPage }>(GET_ACTIVITY, { filter, limit: PAGE_SIZE, offset })
      setTotalCount(res.getActivity.totalCount)
      setEntries((prev) => (append ? [...prev, ...res.getActivity.entries] : res.getActivity.entries))
    } catch (err: any) {
      setError(err?.response?.errors?.[0]?.message || 'Failed to load activity')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchPage(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const clearAll = () => {
    setSearchText('')
    setSearchParams((prev) => {
      for (const key of FILTER_KEYS) prev.delete(key)
      return prev
    })
  }

  const hasFilter = FILTER_KEYS.some((key) => searchParams.get(key))
  // The pinned user, named from the first entry that mentions them
  const pinnedUser = filter.userId
  const pinnedUserEmail = pinnedUser ? (entries.find((e) => e.subjects.userId === pinnedUser)?.subjects.userEmail ?? entries.find((e) => e.actor.userId === pinnedUser)?.actor.email ?? null) : null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          Activity
          {!loading && <span className="text-sm font-normal text-gray-500">({totalCount})</span>}
        </h3>
        {hasFilter && (
          <Button variant="outline" size="sm" onClick={clearAll}>
            <X className="h-4 w-4 mr-1" />
            Clear filters
          </Button>
        )}
      </div>

      {pinnedUser && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm">
          Showing activity of {pinnedUserEmail || `user #${pinnedUser}`}
          <button onClick={() => setFilter('userId', '')} className="hover:text-primary/70" aria-label="Show all users">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Wraps onto more lines as the width shrinks; every control may shrink below its content */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-[2_1_14rem] min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by email or organisation..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
        </div>
        <select
          value={searchParams.get('action') || ''}
          onChange={(e) => setFilter('action', e.target.value)}
          aria-label="Action"
          className="flex-[1_1_11rem] min-w-0 max-w-full truncate px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
        >
          <option value="">All actions</option>
          {actions.map((name) => (
            <option key={name} value={name}>
              {actionLabel(name)}
            </option>
          ))}
        </select>
        <select
          value={searchParams.get('kind') || ''}
          onChange={(e) => setFilter('kind', e.target.value)}
          aria-label="Kind"
          className="flex-[1_1_8rem] min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
        >
          <option value="">Everything</option>
          <option value="mutation">Changes</option>
          <option value="sign_in">Sign-ins</option>
        </select>
        <div className="flex flex-[1_1_16rem] min-w-0 items-center gap-2">
          <input
            type="date"
            value={searchParams.get('from') || ''}
            onChange={(e) => setFilter('from', e.target.value)}
            aria-label="From date"
            className="w-full min-w-0 px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          />
          <input
            type="date"
            value={searchParams.get('to') || ''}
            onChange={(e) => setFilter('to', e.target.value)}
            aria-label="To date"
            className="w-full min-w-0 px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          />
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{hasFilter ? 'Nothing matches these filters' : 'No activity recorded yet'}</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-4 py-3 font-medium">When</th>
                <th className="px-4 py-3 font-medium">Who</th>
                <th className="px-4 py-3 font-medium">Action</th>
                <th className="px-4 py-3 font-medium">Subject</th>
                <th className="px-4 py-3 font-medium">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const open = expanded.has(entry.id)
                return (
                  <React.Fragment key={entry.id}>
                    <tr className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer" onClick={() => toggle(entry.id)} aria-expanded={open}>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                          {formatTime(entry.at)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-800">{entry.actor.email || (entry.actor.userId != null ? `user #${entry.actor.userId}` : '—')}</div>
                        {entry.actor.isAdmin && <span className="text-[10px] uppercase tracking-wide text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">admin</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-800" title={entry.action}>
                        {actionLabel(entry.action)}
                        {entry.kind === 'sign_in' && entry.meta?.provider && <span className="ml-1 text-xs text-gray-500">via {entry.meta.provider}</span>}
                        {entry.kind === 'sign_in' && entry.meta?.createdAccount && <span className="ml-1 text-xs text-green-700">(new account)</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {entry.subjects.userEmail && <div>{entry.subjects.userEmail}</div>}
                        {entry.subjects.organisationName && <div className="text-xs text-gray-500">{entry.subjects.organisationName}</div>}
                        {!entry.subjects.userEmail && !entry.subjects.organisationName && <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {entry.ok ? (
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="sr-only">Succeeded</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700" title={entry.error || undefined}>
                            <AlertCircle className="h-4 w-4 shrink-0" />
                            <span className="text-xs">{entry.error || 'Failed'}</span>
                          </span>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-t border-gray-100 bg-gray-50/40">
                        <td colSpan={5} className="px-6 py-3">
                          <div className="grid gap-2 md:grid-cols-2 text-xs text-gray-600">
                            <div>
                              <div className="font-medium text-gray-700 mb-1">Input</div>
                              <pre className="whitespace-pre-wrap break-all bg-white border border-gray-200 rounded p-2">{entry.input ? JSON.stringify(entry.input, null, 2) : '—'}</pre>
                            </div>
                            <div>
                              <div className="font-medium text-gray-700 mb-1">Details</div>
                              <dl className="space-y-0.5">
                                <div className="flex gap-2">
                                  <dt className="text-gray-500 w-28">Raw action</dt>
                                  <dd className="font-mono">{entry.action}</dd>
                                </div>
                                {entry.durationMs != null && (
                                  <div className="flex gap-2">
                                    <dt className="text-gray-500 w-28">Duration</dt>
                                    <dd>{entry.durationMs} ms</dd>
                                  </div>
                                )}
                                {entry.subjects.userId != null && (
                                  <div className="flex gap-2">
                                    <dt className="text-gray-500 w-28">User id</dt>
                                    <dd>{entry.subjects.userId}</dd>
                                  </div>
                                )}
                                {entry.subjects.organisationId != null && (
                                  <div className="flex gap-2">
                                    <dt className="text-gray-500 w-28">Organisation id</dt>
                                    <dd>{entry.subjects.organisationId}</dd>
                                  </div>
                                )}
                                {entry.subjects.moduleId != null && (
                                  <div className="flex gap-2">
                                    <dt className="text-gray-500 w-28">Module id</dt>
                                    <dd>{entry.subjects.moduleId}</dd>
                                  </div>
                                )}
                                {entry.subjects.subscriptionId != null && (
                                  <div className="flex gap-2">
                                    <dt className="text-gray-500 w-28">Subscription id</dt>
                                    <dd>{entry.subjects.subscriptionId}</dd>
                                  </div>
                                )}
                                {(entry.subjects.joinLinkId != null || entry.meta?.joinLinkId != null) && (
                                  <div className="flex gap-2">
                                    <dt className="text-gray-500 w-28">Join link id</dt>
                                    <dd>{entry.subjects.joinLinkId ?? entry.meta?.joinLinkId}</dd>
                                  </div>
                                )}
                                {entry.error && (
                                  <div className="flex gap-2">
                                    <dt className="text-gray-500 w-28">Error</dt>
                                    <dd className="text-red-700">{entry.error}</dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
          {entries.length < totalCount && (
            <div className="flex items-center justify-center py-4 border-t border-gray-100">
              <Button variant="outline" size="sm" onClick={() => fetchPage(entries.length)} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : `Load more (${totalCount - entries.length} left)`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ActivityView
